import { useState, useEffect, useMemo } from 'react';
import BasicPopover from 'components/Popover/basic';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-regular-svg-icons';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { Box, Typography, Stack, IconButton, Button } from '@mui/material';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import NotificationItem from './notification.item';
import { NormalText } from 'components/styled/text.styled';
import { Badge } from '@mui/material';
import {
  NotificationProps,
  notificationClearCounter,
  getNotifications,
  updateNotificationReadStatus,
  getUnreadNotifications,
} from 'redux/notification';
import useRedux from 'redux/useRedux';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import ApiService from 'services/ApiService';
import { formatRelativeTime } from 'helper/generalFunction';

interface NotificationsProps {
  notifications?: NotificationProps[];
  counter?: number;
  userRole?: string;
}

const NotificationSection = ({ notifications, counter, userRole }: NotificationsProps) => {
  const {
    thunkDispatch,
    storeState: { Auth, Notification },
  } = useRedux();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const navigate = useNavigate();

  const { allNotifications, isFetching, isError, message, unreadNotifications } = Notification;
  const merchantId = Auth?.data?.data?.merchant?.id;
  const token = Auth?.data?.data?.token;
  
  // Superadmin State
  const [saNotifications, setSaNotifications] = useState<any[]>([]);
  const [saFetching, setSaFetching] = useState(false);
  const [saError, setSaError] = useState(false);
  const [saMessage, setSaMessage] = useState('');
  const [saReadIds, setSaReadIds] = useState<Set<string>>(new Set());
  const [saTotal, setSaTotal] = useState(0);

  useEffect(() => {
    if (userRole === 'ROLE_SUPER_ADMIN') {
      const stored = localStorage.getItem('superadmin_read_notifications');
      if (stored) {
        try {
          setSaReadIds(new Set(JSON.parse(stored)));
        } catch (e) {
          console.error('Failed to parse superadmin read notifications', e);
        }
      }
    }
  }, [userRole]);

  const fetchSuperadminNotifications = async () => {
    if (!token) return;
    setSaFetching(true);
    setSaError(false);
    try {
      const apiUrl = process.env.REACT_APP_NEST_API_URL;
      const response = await ApiService.get(`${apiUrl}/api/v1/superadmin-notification?page_no=1&page_size=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && response.data.metaData && response.data.metaData.status === 'true') {
        setSaNotifications(response.data.data.notifications || []);
        if (response.data.data.pageData?.totalElements) {
          setSaTotal(response.data.data.pageData.totalElements);
        }
      } else {
        setSaError(true);
        setSaMessage('Gagal memuat notifikasi');
      }
    } catch (error: any) {
      setSaError(true);
      setSaMessage(error.message || 'Terjadi kesalahan');
    } finally {
      setSaFetching(false);
    }
  };

  useEffect(() => {
    if (userRole === 'ROLE_SUPER_ADMIN' && token) {
      fetchSuperadminNotifications();
    }
  }, [userRole, token]);


  useEffect(() => {
    if (userRole !== 'ROLE_SUPER_ADMIN' && merchantId && token) {
      thunkDispatch(
        getNotifications({
          merchantId,
          token,
          page_no: 1,
          page_size: 10,
          sort_by: 'created_at',
          sort_direction: 'asc',
        }),
      );
    }
  }, [merchantId, thunkDispatch, token, userRole]);

  useEffect(() => {
    if (userRole !== 'ROLE_SUPER_ADMIN' && counter && counter > 0 && merchantId && token) {
      console.log('firebase notification received, refreshing notification...');
      thunkDispatch(
        getNotifications({
          merchantId,
          token,
          page_no: 1,
          page_size: 10,
          sort_by: 'created_at',
          sort_direction: 'desc',
        }),
      );
    }
  }, [counter, merchantId, thunkDispatch, token, userRole]);

 const unreadCount = useMemo(() => {
  if (userRole === 'ROLE_SUPER_ADMIN') {
    return Math.max(0, saTotal - saReadIds.size);
  }
  if (unreadNotifications && unreadNotifications.length > 0) {
    return unreadNotifications.length
  }
  return 0
}, [userRole, saTotal, saReadIds, unreadNotifications]);


  useEffect(() => {
    if (userRole !== 'ROLE_SUPER_ADMIN' && merchantId && token) {
      thunkDispatch(getUnreadNotifications({ merchantId, token }));
    }
  }, [merchantId, token, thunkDispatch, userRole]);


const displayNotifications = useMemo(() => {
  if (userRole === 'ROLE_SUPER_ADMIN') {
    return []; 
  }

  let combinedNotifications: NotificationProps[] = [];

  if (allNotifications && allNotifications.length > 0) {
    combinedNotifications = [...allNotifications];
  }

  // Include new notifications that aren't already in the list
  if (notifications && notifications.length > 0) {
    const existingIds = new Set(combinedNotifications.map((n) => n.id));
    const newFirebaseNotifications = notifications.filter((n) => !existingIds.has(n.id));
    combinedNotifications = [...newFirebaseNotifications, ...combinedNotifications];
  }

  return combinedNotifications
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5); // Adjusting to show the latest 5 notifications
}, [allNotifications, notifications, userRole]);

  const handleClearCounter = () => {
    if (userRole !== 'ROLE_SUPER_ADMIN') {
      thunkDispatch(notificationClearCounter());
    }
  };

  const handleMarkAsRead = (notificationId: number) => {
    if (token && merchantId) {
      thunkDispatch(
        updateNotificationReadStatus({
          notificationId,
          isRead: true,
          token,
          markAll: false,
        }),
      )
        .unwrap()
        .then((res) => {
          if (res && res.status === 'success') {
            thunkDispatch(
              getNotifications({
                merchantId,
                token,
                page_no: 1,
                page_size: 10,
                sort_by: 'created_at',
                sort_direction: 'desc',
              }),
            );
            thunkDispatch(getUnreadNotifications({ merchantId, token }));
          }
        })
        .catch((err) => {
          console.error('Error marking notification as read:', err);
        });
    }
  };

  const handleMarkAllAsRead = () => {
    if (isSuperadmin) {
      if (saNotifications.length > 0) {
        const newIds = new Set(saReadIds);
        saNotifications.forEach((n) => newIds.add(n.id));
        localStorage.setItem('superadmin_read_notifications', JSON.stringify(Array.from(newIds)));
        setSaReadIds(newIds);
      }
      return;
    }

    if (token && unreadCount > 0) {
      setMarkingAllAsRead(true);
      thunkDispatch(
        updateNotificationReadStatus({
          isRead: true,
          token,
          markAll: true,
          merchantId,
        }),
      )
        .unwrap()
        .then((res) => {
          if (res && res.status === 'success') {
            thunkDispatch(
              getNotifications({
                merchantId,
                token,
                page_no: 1,
                page_size: 10,
                sort_by: 'created_at',
                sort_direction: 'desc',
              }),
            );
            thunkDispatch(getUnreadNotifications({ merchantId, token }));
          }
        })
        .catch((err) => {
          console.error('Error marking all notifications as read:', err);
        })
        .finally(() => {
          setMarkingAllAsRead(false);
        });
    }
  };
  
  const handleSaNotificationClick = (notification: any) => {
    if (!saReadIds.has(notification.id)) {
      const newIds = new Set(saReadIds);
      newIds.add(notification.id);
      localStorage.setItem('superadmin_read_notifications', JSON.stringify(Array.from(newIds)));
      setSaReadIds(newIds);
    }
    
    setOpen(false);
    
    if (notification.type === 'REGISTRATION') {
      navigate('/merchant/view', { state: { data: notification.merchantData, status: 'view' } });
    } else if (notification.type === 'READY') {
      navigate('/merchant/edit-merchant', { state: { data: notification.merchantData, statusUrl: 'edit' } });
    }
  };

  // Handle bell icon click - triggers API call
  const handleBellClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    console.log('Bell icon clicked, fetching latest notifications...');

    if (userRole === 'ROLE_SUPER_ADMIN') {
      fetchSuperadminNotifications();
    } else {
      // Always fetch latest notifications when bell is clicked
      if (merchantId && token) {
        thunkDispatch(
          getNotifications({
            merchantId,
            token,
            page_no: 1,
            page_size: 10,
            sort_by: 'created_at',
            sort_direction: 'desc',
          }),
        );
      }
  
      // Clear Firebase counter when user opens the notification panel
      if (counter && counter > 0) {
        handleClearCounter();
      }
    }

    // Toggle popover
    setOpen(!open);
    setAnchor(e.currentTarget);
  };
  
  const isSuperadmin = userRole === 'ROLE_SUPER_ADMIN';
  const showFetching = isSuperadmin ? saFetching : isFetching;
  const showError = isSuperadmin ? saError : isError;
  const errorMessage = isSuperadmin ? saMessage : message;

  return (
    <>
      <StyledButton
        onClick={(e) => {
          if (!open) {
            handleBellClick(e);
          } else {
            setOpen(false);
            setAnchor(null);
          }
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ff4444',
              color: 'white',
            },
          }}
        >
          <NotificationsNoneOutlinedIcon fontSize="medium" />
        </Badge>
      </StyledButton>
      <BasicPopover
        id="notification popover"
        anchorEl={anchor}
        open={open}
        handleClose={() => {
          setOpen(!open);
          setAnchor(null);
        }}
      >
        <Stack width={'400px'} direction="column" padding={'1rem 1rem 2rem 1rem'}>
          <Stack
            direction="row"
            justifyContent={'space-between'}
            alignItems={'center'}
            sx={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '8px' }}
          >
            <Typography variant="h6" fontSize={'1rem'} fontWeight={'bold'}>
              {isSuperadmin ? 'Notifikasi Superadmin' : 'Notifikasi Terbaru'}
            </Typography>
            <IconButton
              onClick={() => {
                setOpen(false);
              }}
              size="small"
            >
              <FontAwesomeIcon color={'rgba(0, 0, 0, 0.5)'} icon={faXmark} />
            </IconButton>
          </Stack>

          {/* Notification List */}
          <Stack direction={'column'} className="mt-3" spacing={0.5}>
            {' '}
            {/* Reduced spacing from 1 to 0.5 */}
            {showFetching ? (
              <Box sx={{ textAlign: 'center', padding: '20px' }}>
                <NormalText fontSize="12px" color={'rgba(0, 0, 0, 0.5)'}>
                  Memuat notifikasi...
                </NormalText>
              </Box>
            ) : showError ? (
              <Box sx={{ textAlign: 'center', padding: '20px' }}>
                <NormalText fontSize="12px" color="red">
                  Error: {errorMessage}
                </NormalText>
              </Box>
            ) : isSuperadmin && saNotifications && saNotifications.length > 0 ? (
               saNotifications.slice(0, 5).map((notification, index) => {
                 const isRead = saReadIds.has(notification.id);
                 return (
                  <Box
                    key={notification.id || index}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: isRead ? 'transparent' : '#f5f5f5',
                      borderRadius: '4px',
                      padding: '4px 6px',
                      transition: 'background-color 0.2s ease',
                      minHeight: 'auto',
                      '&:hover': {
                        backgroundColor: isRead ? '#f9f9f9' : '#e8e8e8',
                      },
                    }}
                  >
                    <NotificationItem
                      customerName={notification.title}
                      orderDate={formatRelativeTime(notification.createdAt)}
                      bodyMessage={notification.body}
                      isRead={isRead}
                      onClick={() => handleSaNotificationClick(notification)}
                    />
                  </Box>
                 );
               })
            ) : !isSuperadmin && displayNotifications && displayNotifications.length > 0 ? (
              displayNotifications.map((notification, index) => (
                <Box
                  key={notification.id || index}
                  onClick={() => !notification.isRead && handleMarkAsRead(notification.id)}
                  sx={{
                    cursor: !notification.isRead ? 'pointer' : 'default',
                    backgroundColor: notification.isRead ? 'transparent' : '#f5f5f5',
                    borderRadius: '4px', // Reduced from 8px
                    padding: '4px 6px', // Reduced from '8px'
                    transition: 'background-color 0.2s ease',
                    minHeight: 'auto', // Ensure minimal height
                    '&:hover': {
                      backgroundColor: notification.isRead ? '#f9f9f9' : '#e8e8e8',
                    },
                  }}
                >
                  <NotificationItem
                    customerName={notification?.customerName}
                    amountPurchasing={Number(notification?.price)}
                    orderId={notification?.orderId}
                    price={notification?.price}
                    orderDate={formatRelativeTime(notification?.createdAt)}
                    bodyMessage={notification?.body ?? ''}
                    isRead={notification?.isRead}
                  />
                </Box>
              ))
            ) : (
              <Box sx={{ textAlign: 'center', padding: '20px' }}>
                <NormalText fontSize="12px" color={'rgba(0, 0, 0, 0.5)'}>
                  Tidak ada notifikasi.
                </NormalText>
              </Box>
            )}
          </Stack>

          {/* Show unread count if any */}
          {unreadCount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '8px', gap: '12px' }}>
              <NormalText fontSize="11px" color={'rgba(0, 0, 0, 0.6)'}>
                {unreadCount} notifikasi belum dibaca
              </NormalText>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: '#2ec291', 
                  cursor: markingAllAsRead ? 'default' : 'pointer', 
                  fontWeight: 'bold', 
                  '&:hover': { textDecoration: markingAllAsRead ? 'none' : 'underline' } 
                }}
                onClick={markingAllAsRead ? undefined : handleMarkAllAsRead}
              >
                {markingAllAsRead ? 'Memproses...' : 'Tandai Semua Dibaca'}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* View All Button */}
        <Button
          type="button"
          onClick={() => {
            setOpen(false);
            navigate('/notifications');
          }}
          sx={{
            width: '100%',
            background: '#2ec291',
            borderRadius: 0,
            color: '#ffffff',
            fontWeight: 'bold',
            textTransform: 'none',
            padding: '8px',
            '&:hover': {
              backgroundColor: '#2db588',
              color: '#ffffff',
            },
          }}
        >
          Lihat Semua Notifikasi
        </Button>
      </BasicPopover>
    </>
  );
};

const StyledButton = styled.button`
  border: none;
  background: transparent;
  width: 40px;
  height: 40px;
  position: relative;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  svg {
    color: rgba(0, 0, 0, 0.5);
    transition: color 0.2s ease;
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
    svg {
      color: #000;
    }
  }
`;

export default NotificationSection;
